import React from 'react'

import {
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { LockIcon, MessagesSquareIcon } from 'lucide-react'

import { Card, Skeleton, EMPTY_VALUE } from '@repo/ui'
import { EmptyState, InfoBlock, InfoItem, InfoRow } from 'components'
import { useCompanyAskStats } from 'hooks/api'
import { relativeOrDateTime } from 'utils'

/**
 * One company's Ask Vera activity — the Ask tab of the internal company detail page
 * (VRG-516, against the endpoint VRG-515 built).
 *
 * ## Why this is not the cross-company Ask page, scoped
 *
 * `/internal/ask-stats` exists to answer "where is Vera's documentation failing" — it
 * is built out of coverage failures, cited guides and glossary terms. Scoped to one
 * company those sections are near-empty and mostly not that company's business, so the
 * endpoint returns a smaller, different shape and this panel renders that. §71 asks a
 * tab to share its standalone page's TABLE where the two show the same object; here
 * they genuinely do not, and forcing the shape would produce a page of blanks.
 *
 * ## What the granularities actually permit
 *
 *  - `ask_query_log` is an append-only event log, so `dailySeries` is a real daily
 *    series. Days with no questions are ABSENT from it rather than zero-filled, so it
 *    must not be read as contiguous — which is why the summary below prints "days
 *    with activity" rather than drawing a line with invented gaps.
 *  - `usage` is a per-(company, month) counter from the limiter, so it is MONTH
 *    granularity and cannot be broken down further. It counts ANSWERED questions
 *    against the plan pool, while the log also records refusals and errors — the two
 *    totals are expected to differ, and the copy says so rather than leaving a reader
 *    to think one of them is wrong.
 */

interface CompanyAskPanelProps {
  companyId: string
}

const CompanyAskPanelSkeleton = () => (
  <Stack spacing={4}>
    <InfoBlock>
      <InfoRow columns={4}>
        <InfoItem label="Questions" value={undefined} />
        <InfoItem label="People asking" value={undefined} />
        <InfoItem label="Days with activity" value={undefined} />
        <InfoItem label="Plan pool used" value={undefined} />
      </InfoRow>
    </InfoBlock>
    <Card title="Most asked">
      <Stack spacing={3} sx={{ mt: 4 }}>
        <Skeleton height={14} lines={4} />
      </Stack>
    </Card>
  </Stack>
)

export const CompanyAskPanel = ({ companyId }: CompanyAskPanelProps) => {
  const theme = useTheme()
  const { data, isLoading, error } = useCompanyAskStats(companyId)

  if (isLoading) return <CompanyAskPanelSkeleton />

  if (error) {
    const status = (error as Error & { status?: number }).status
    return (
      <Card>
        <EmptyState
          icon={
            <LockIcon size={28} strokeWidth={1} color={theme.palette.text.secondary} />
          }
          title={status === 403 ? 'You cannot view Ask activity' : 'Could not load Ask activity'}
          text={
            status === 403
              ? 'Ask activity needs the "View internal analytics" permission. Ask a platform admin to grant it.'
              : 'The request failed. Reload the page, or check the API is running.'
          }
          sx={{ py: 10 }}
        />
      </Card>
    )
  }

  const stats = data?.stats
  const usage = data?.usage

  if (!stats || stats.totalQuestions === 0) {
    return (
      <Card>
        <EmptyState
          icon={
            <MessagesSquareIcon
              size={28}
              strokeWidth={1}
              color={theme.palette.text.secondary}
            />
          }
          title="No Ask questions from this company"
          text="Nobody here has asked Vera anything yet. The log keeps 90 days, so this is what has happened recently rather than for all time."
          sx={{ py: 10 }}
        />
      </Card>
    )
  }

  const outcomes = Object.entries(stats.byOutcome)

  return (
    <Stack spacing={4}>
      <InfoBlock>
        {/* Same 4-column grid as the Details tab, so the two tabs read as one page
            rather than as two different layouts wearing the same chrome. */}
        <InfoRow columns={4}>
          <InfoItem label="Questions" value={stats.totalQuestions} />
          <InfoItem label="People asking" value={stats.distinctUsers} />
          {/* Days WITH ACTIVITY, not "days" — `dailySeries` omits empty days entirely,
              so its length is a count of active days and calling it anything else
              would overstate it. */}
          <InfoItem label="Days with activity" value={stats.dailySeries.length} />
          <InfoItem
            label="Plan pool used"
            // A dash, not "0 / 0", when the plan lookup failed. The endpoint returns
            // `usage: null` in that case rather than failing the whole tab, and a
            // fabricated 0 would read as "this company has used nothing".
            value={usage ? `${usage.used} / ${usage.limit}` : null}
          />
        </InfoRow>
      </InfoBlock>

      <Card title="Most asked">
        <Stack spacing={4} sx={{ mt: 4 }}>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Grouped by the exact question text, most asked first. Questions are stored
            verbatim, so two phrasings of the same thing count separately.
          </Typography>
          <MuiTable size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                  Question
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}
                >
                  Asked
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.topQuestions.map(row => (
                <TableRow key={row.question}>
                  <TableCell sx={{ fontSize: 13 }}>{row.question}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    {row.count}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        </Stack>
      </Card>

      <Card title="Outcomes">
        <Stack spacing={4} sx={{ mt: 4 }}>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Every logged question, by what happened to it. This counts refusals and
            errors as well as answers, so the total here can exceed the plan pool figure
            above, which counts answered questions only.
          </Typography>
          <MuiTable size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                  Outcome
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}
                >
                  Questions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outcomes.map(([outcome, count]) => (
                <TableRow key={outcome}>
                  <TableCell sx={{ fontSize: 13, textTransform: 'capitalize' }}>
                    {outcome.replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>
                    {count}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {stats.tokens.input.toLocaleString()} input ·{' '}
            {stats.tokens.output.toLocaleString()} output tokens
            {usage?.resetsAt
              ? ` · pool resets ${relativeOrDateTime(usage.resetsAt)}`
              : ''}
            {stats.computedAt
              ? ` · computed ${relativeOrDateTime(stats.computedAt)}`
              : ` · ${EMPTY_VALUE}`}
          </Typography>
        </Stack>
      </Card>
    </Stack>
  )
}
